/**
 * The lightbulb's item-first brief (GET /api/jobs/:id/brief) and the "opened the hints"
 * flag that gates Start.
 *
 * One in-memory cache per job, shared by the job screens: the brief is prefetched in the
 * background once the job loads and read from here when the bulb opens, so it is usually
 * on screen instantly. Every call fails soft: no brief means the bulb shows the category
 * guide it always had.
 */
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api } from './api';

export type BriefLesson = { text: string; when: string | null; scope: 'model' | 'category' };
export type JobBrief = {
  walkIn: string[];
  itemFacts: string[];
  lessons: BriefLesson[];
  /** The live listing, to check what the buyer was promised. */
  listingUrl: string | null;
  meta: { modelKey: string | null; lessonScope: 'model' | 'category' | 'none'; lessonJobs: number; briefViewedAt: string | null } | null;
};

const cache = new Map<string, { at: number; p: Promise<JobBrief | null> }>();
const TTL_MS = 5 * 60_000;

const clean = (d: any): JobBrief | null => {
  if (!d || typeof d !== 'object') return null;
  const strs = (a: any) => (Array.isArray(a) ? a.filter((x) => typeof x === 'string' && x.trim()) : []);
  return {
    walkIn: strs(d.walkIn).slice(0, 5),
    itemFacts: strs(d.itemFacts),
    lessons: (Array.isArray(d.lessons) ? d.lessons : []).filter((l: any) => l && typeof l.text === 'string').slice(0, 4),
    listingUrl: typeof d.listingUrl === 'string' && /^https:\/\//.test(d.listingUrl) ? d.listingUrl : null,
    meta: d.meta || null,
  };
};

/** The brief for a job; the same promise for every caller within 5 minutes. */
export function fetchJobBrief(jobId: string, opts?: { fresh?: boolean }): Promise<JobBrief | null> {
  if (!jobId) return Promise.resolve(null);
  const hit = cache.get(jobId);
  if (hit && !opts?.fresh && Date.now() - hit.at < TTL_MS) return hit.p;
  const p = api.get(`/jobs/${jobId}/brief`).then((r) => clean(r.data)).catch(() => null);
  cache.set(jobId, { at: Date.now(), p });
  return p;
}

/** Warm the cache without anyone waiting on it. */
export function prefetchJobBrief(jobId: string | null | undefined): void {
  if (jobId) fetchJobBrief(jobId).catch(() => {});
}

const viewedKey = (jobId: string) => `tips_viewed_${jobId}`;

/** Has the driver opened the lightbulb for this job? Local flag OR the server's record. */
export async function briefWasViewed(jobId: string, job?: { briefViewedAt?: string | null } | null): Promise<boolean> {
  if (job?.briefViewedAt) return true;
  try { if ((await AsyncStorage.getItem(viewedKey(jobId))) === 'true') return true; } catch { /* storage can be blocked */ }
  return false;
}

/** The bulb was opened: remember it here and on the server (survives a reinstall). */
export function markJobBriefViewed(jobId: string | null | undefined): void {
  if (!jobId) return;
  AsyncStorage.setItem(viewedKey(jobId), 'true').catch(() => {});
  api.post(`/jobs/${jobId}/brief-viewed`).catch(() => {});
}
